import {
  useModules,
  useRecipe,
  useRecordConsumableReplenishment,
  useRecordReagentReplenishment,
  useRecordWasteReplacement,
  useWasteContainers,
  type ApiError,
} from '@ctcells/api'
import type { TrayPath } from '@ctcells/contract'
import { t } from '@ctcells/i18n'

import { quickFailureText } from './quickResetPorts.ts'
import {
  QUICK_SUPPLY_NOTE,
  QUICK_WASTE_NOTE,
  quickReagentPlan,
  quickSupplyPlan,
  quickTrayPath,
  quickWastePlan,
  type QuickSupplyKind,
} from './devQuickBatch.ts'

export type QuickSupplyResult =
  { refused: string } | { refused?: undefined; trayPath: TrayPath | undefined }

/**
 * **소모품 · 시약 보충 + 폐기통 교체 기록**(개발용 자동 준비의 한 걸음) — 「테스트 작업 시작」과 작업 줄 [시작]의 시뮬 준비
 * (`useSimStartPrepare`)가 같이 쓴다. 판단 함수(`quickSupplyPlan` · `quickReagentPlan` · `quickWastePlan`)는 `devQuickBatch.ts` 에 있다.
 * `live` 가 거짓이면 아무 조회도 하지 않는다(목 화면이 실서버를 때리지 않는다).
 */
export function useQuickSupply({
  live,
  recipeId,
  notes,
}: {
  live: boolean
  recipeId: string | undefined
  /**
   * 원장 표지 머리 — 보충(`supply`) · 폐기통 교체(`waste`). 비우면 개발용 표지(`QUICK_SUPPLY_NOTE` · `QUICK_WASTE_NOTE`).
   * 시작 창의 [한 번에 준비]는 개발용이라 적지 않는다 — 사람이 체크한 실제 보충 기록이다
   */
  notes?: { supply: string; waste: string } | undefined
}): (tag: string, recipeOverride?: { payload: unknown }) => Promise<QuickSupplyResult> {
  const supplyNote = notes?.supply ?? QUICK_SUPPLY_NOTE
  const wasteNote = notes?.waste ?? QUICK_WASTE_NOTE
  const replenish = useRecordConsumableReplenishment()
  const replenishReagent = useRecordReagentReplenishment()
  const recordWaste = useRecordWasteReplacement()
  /*
   * 보충 판단의 두 입력 — 모듈 잔량과 이 회차 레시피의 랩웨어 칸 선언. 🔴 **누를 때 한 번 다시 받는다**
   * (`refetch`) — 캐시가 몇 분 묵었으면 「이미 가득」을 잘못 판정한다. 주기 폴링이 아니라 사용자 행위 1회다.
   */
  const modules = useModules(live ? {} : { skip: true })
  const recipe = useRecipe({ recipeId: live ? recipeId : undefined })
  const wasteContainers = useWasteContainers(live ? {} : { skip: true })

  /**
   * 소모품 보충 — 모자란 것만 레시피가 선언한 칸 수까지 · **폐기통 교체 기록** — 서버가 「비워야 함」이라 한 통만
   * (`quickWastePlan` · 0.9.3 점검 `PREFLIGHT_WASTE_NOT_EMPTIED`). 실패하면 **사유 문자열**을 돌려준다(`undefined` = 통과).
   * 🔴 한 곳이라도 실패하면 거기서 멈춘다 — 작업을 만들어도 사전 점검이 같은 사유로 막는다.
   */
  return async (tag: string, recipeOverride?: { payload: unknown }): Promise<QuickSupplyResult> => {
    /* 레시피 본문을 이미 쥐고 왔으면(서버 표준으로 고른 회차 · `useQuickRecipe`) 다시 받지 않는다 */
    const [moduleResult, recipeResult, wasteResult] = await Promise.all([
      modules.refetch(),
      recipeOverride === undefined
        ? recipe.refetch()
        : Promise.resolve({ data: { payload: recipeOverride.payload }, error: null }),
      wasteContainers.refetch(),
    ])
    if (moduleResult.error) return { refused: failureText(moduleResult.error) }
    if (recipeResult.error) return { refused: failureText(recipeResult.error) }
    if (wasteResult.error) return { refused: failureText(wasteResult.error) }
    const plan = quickSupplyPlan(moduleResult.data?.items ?? [], recipeResult.data?.payload)
    const uncounted = plan.uncounted[0]
    if (uncounted !== undefined)
      return {
        refused: t('dev.quickSupply.uncounted', {
          module: uncounted.moduleId,
          kind: kindLabel(uncounted.kind),
        }),
      }
    for (const step of plan.steps) {
      try {
        await replenish.mutateAsync({
          moduleId: step.moduleId,
          kind: step.kind,
          count: step.count,
          // 🔴 원장에서 가려 읽는 표지 — append-only 라 지울 수 없으니 누가 왜 채웠는지를 남긴다
          note: `${supplyNote} · ${tag}`,
        })
      } catch (error) {
        return {
          refused: `${step.moduleId} ${kindLabel(step.kind)} ${failureText(error as ApiError)}`,
        }
      }
    }
    /* 시약 — 레시피 `loadLiquid` 양보다 적은 웰만(`quickReagentPlan` 머리말 · 소진 보류가 L 이송을 멈췄다) */
    for (const step of quickReagentPlan(
      moduleResult.data?.items ?? [],
      recipeResult.data?.payload,
    )) {
      try {
        await replenishReagent.mutateAsync({
          moduleId: step.moduleId,
          labwareId: step.labwareId,
          wellNames: step.wellNames,
          volume: step.volume,
          note: `${supplyNote} · ${tag}`,
        })
      } catch (error) {
        return {
          refused: `${step.moduleId} ${t('dev.quickSupply.kind.reagent')} ${failureText(error as ApiError)}`,
        }
      }
    }
    for (const containerId of quickWastePlan(wasteResult.data?.items ?? [])) {
      try {
        // 🔴 원장에서 가려 읽는 표지 — append-only 라 지울 수 없으니 누가 왜 적었는지를 남긴다
        await recordWaste.mutateAsync({ containerId, note: `${wasteNote} · ${tag}` })
      } catch (error) {
        return {
          refused: `${containerId} ${t('dev.quickSupply.kind.waste')} ${failureText(error as ApiError)}`,
        }
      }
    }
    // 웰 배정이 읽을 레시피 묶음 — 같은 refetch 결과다(한 번 더 받지 않는다)
    // 옛 형식은 `tray_path` 그대로 · 새 형식은 트레이 시료 액체로 같은 꼴을 만든다(`quickTrayPath`)
    return { trayPath: quickTrayPath(recipeResult.data?.payload) }
  }
}

function kindLabel(kind: QuickSupplyKind): string {
  return t(kind === 'tip' ? 'dev.quickSupply.kind.tip' : 'dev.quickSupply.kind.tc')
}

const failureText = quickFailureText
